'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

const agencies = [
  { id: 'MOH', name: 'Kementerian Kesihatan Malaysia', short: 'MOH', category: 'Health', services: 12, openCases: 284, sla: '96.2%', status: 'Active', integration: 'Connected', color: 'bg-green-100 text-green-700', escalation: 'Health Services Group', queue: 'Health Services', articles: 48 },
  { id: 'IMM', name: 'Jabatan Imigresen Malaysia', short: 'Immigration', category: 'Immigration', services: 8, openCases: 412, sla: '88.4%', status: 'Active', integration: 'Connected', color: 'bg-blue-100 text-blue-700', escalation: 'Immigration Group', queue: 'Immigration', articles: 32 },
  { id: 'JPJ', name: 'Jabatan Pengangkutan Jalan', short: 'JPJ', category: 'Transport', services: 15, openCases: 198, sla: '94.1%', status: 'Active', integration: 'Connected', color: 'bg-orange-100 text-orange-700', escalation: 'Transport Group', queue: 'Transport', articles: 27 },
  { id: 'JPN', name: 'Jabatan Pendaftaran Negara', short: 'JPN', category: 'Registration', services: 10, openCases: 156, sla: '97.3%', status: 'Active', integration: 'Connected', color: 'bg-purple-100 text-purple-700', escalation: 'Registration Group', queue: 'Digital Services', articles: 21 },
  { id: 'LHDN', name: 'Lembaga Hasil Dalam Negeri', short: 'LHDN', category: 'Finance', services: 18, openCases: 321, sla: '91.8%', status: 'Active', integration: 'Connected', color: 'bg-yellow-100 text-yellow-700', escalation: 'Finance Group', queue: 'Taxation', articles: 56 },
  { id: 'KWSP', name: 'Kumpulan Wang Simpanan Pekerja', short: 'KWSP', category: 'Finance', services: 14, openCases: 243, sla: '93.5%', status: 'Active', integration: 'Connected', color: 'bg-teal-100 text-teal-700', escalation: 'Finance Group', queue: 'Finance', articles: 38 },
  { id: 'PERKESO', name: 'Pertubuhan Keselamatan Sosial', short: 'PERKESO', category: 'Social Security', services: 9, openCases: 87, sla: '95.6%', status: 'Active', integration: 'Connected', color: 'bg-red-100 text-red-700', escalation: 'Social Security Group', queue: 'Finance', articles: 19 },
  { id: 'KPKT', name: 'Kementerian Perumahan & Kerajaan Tempatan', short: 'KPKT', category: 'Housing', services: 7, openCases: 134, sla: '89.2%', status: 'Active', integration: 'Partial', color: 'bg-indigo-100 text-indigo-700', escalation: 'Housing Group', queue: 'Government Assistance', articles: 14 },
];

export default function AgenciesContent() {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');

  const filtered = agencies.filter(a =>
    !search || a.name.toLowerCase().includes(search.toLowerCase()) || a.short.toLowerCase().includes(search.toLowerCase()) || a.category.toLowerCase().includes(search.toLowerCase())
  );

  const selectedAgency = agencies.find(a => a.id === selected);

  return (
    <div className="max-w-screen-2xl mx-auto px-5 py-4 page-transition">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[18px] font-700 text-foreground">Agency Directory</h1>
          <p className="text-[12px] text-muted-foreground mt-0.5">{agencies.length} agencies · All integrated with MyGCC</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search agencies..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-field pl-8 w-52"
            />
          </div>
          <div className="flex items-center bg-muted/50 rounded-xl p-1">
            {(['grid', 'list'] as const).map(v => (
              <button key={v} onClick={() => setView(v)} className={`p-1.5 rounded-lg transition-colors ${view === v ? 'bg-white shadow-sm' : 'hover:bg-muted'}`}>
                <Icon name={v === 'grid' ? 'Squares2X2Icon' : 'ListBulletIcon'} size={14} className={view === v ? 'text-primary' : 'text-muted-foreground'} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Agency list/grid */}
        <div className={`flex-1 ${selected ? 'xl:max-w-[60%]' : ''}`}>
          {view === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map(a => (
                <button
                  key={a.id}
                  onClick={() => setSelected(selected === a.id ? null : a.id)}
                  className={`card-elevated p-4 text-left card-hover transition-all ${selected === a.id ? 'border-2 border-primary' : ''}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`px-2.5 py-1 rounded-lg text-[11px] font-700 ${a.color}`}>{a.short}</div>
                    <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-md ${a.integration === 'Connected' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                      {a.integration}
                    </span>
                  </div>
                  <div className="text-[13px] font-700 text-foreground mb-0.5 leading-snug">{a.name}</div>
                  <div className="text-[11px] text-muted-foreground mb-3">{a.category}</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[
                      { label: 'Services', value: a.services },
                      { label: 'Open Cases', value: a.openCases },
                      { label: 'SLA', value: a.sla },
                    ].map(s => (
                      <div key={s.label} className="bg-muted/50 rounded-lg p-2">
                        <div className="text-[14px] font-700 font-tabular text-foreground">{s.value}</div>
                        <div className="text-[9px] text-muted-foreground">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="card-elevated overflow-hidden">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-muted/40 border-b border-border">
                    {['Agency', 'Category', 'Services', 'Open Cases', 'SLA', 'Integration', 'Queue'].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-[10px] font-700 uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                    ))}
                    <th className="px-3 py-2.5 w-16" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map(a => (
                    <tr key={a.id} className="table-row-hover transition-colors cursor-pointer" onClick={() => setSelected(selected === a.id ? null : a.id)}>
                      <td className="px-3 py-3">
                        <div className="font-700 text-foreground">{a.name}</div>
                        <div className="text-[10px] text-muted-foreground">{a.short}</div>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">{a.category}</td>
                      <td className="px-3 py-3 font-tabular font-600 text-foreground">{a.services}</td>
                      <td className="px-3 py-3 font-tabular font-600 text-foreground">{a.openCases}</td>
                      <td className="px-3 py-3 font-700 text-green-600">{a.sla}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-md ${a.integration === 'Connected' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{a.integration}</span>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">{a.queue}</td>
                      <td className="px-3 py-3">
                        <button onClick={e => { e.stopPropagation(); toast.info(`Opening ${a.name}`); }} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                          <Icon name="ArrowTopRightOnSquareIcon" size={13} className="text-primary" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Agency detail panel */}
        {selectedAgency && (
          <div className="w-80 flex-shrink-0 slide-in-right">
            <div className="card-elevated p-4 sticky top-4">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className={`inline-block px-2.5 py-1 rounded-lg text-[11px] font-700 mb-2 ${selectedAgency.color}`}>{selectedAgency.short}</div>
                  <h3 className="text-[14px] font-700 text-foreground leading-snug">{selectedAgency.name}</h3>
                </div>
                <button onClick={() => setSelected(null)} className="p-1 rounded-lg hover:bg-muted transition-colors">
                  <Icon name="XMarkIcon" size={14} className="text-muted-foreground" />
                </button>
              </div>

              <div className="space-y-3 mb-4">
                {[
                  { label: 'Category', value: selectedAgency.category },
                  { label: 'Escalation Group', value: selectedAgency.escalation },
                  { label: 'Routing Queue', value: selectedAgency.queue },
                  { label: 'Knowledge Articles', value: String(selectedAgency.articles) },
                  { label: 'Integration Status', value: selectedAgency.integration },
                ].map(f => (
                  <div key={f.label} className="flex items-center justify-between text-[12px]">
                    <span className="text-muted-foreground">{f.label}</span>
                    <span className="font-600 text-foreground">{f.value}</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { label: 'Services', value: selectedAgency.services, color: 'text-blue-600' },
                  { label: 'Open Cases', value: selectedAgency.openCases, color: 'text-amber-600' },
                  { label: 'SLA', value: selectedAgency.sla, color: 'text-green-600' },
                ].map(s => (
                  <div key={s.label} className="bg-muted/50 rounded-xl p-2.5 text-center">
                    <div className={`text-[16px] font-700 font-tabular ${s.color}`}>{s.value}</div>
                    <div className="text-[9px] text-muted-foreground">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                {[
                  { label: 'View Knowledge Articles', icon: 'BookOpenIcon' },
                  { label: 'View Open Cases', icon: 'ClipboardDocumentListIcon' },
                  { label: 'Escalate to Agency', icon: 'ArrowUpCircleIcon' },
                  { label: 'Agency Contact Directory', icon: 'PhoneIcon' },
                ].map(action => (
                  <button
                    key={action.label}
                    onClick={() => toast.info(action.label)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-border hover:bg-muted transition-colors text-[12px] font-500 text-foreground"
                  >
                    <Icon name={action.icon as Parameters<typeof Icon>[0]['name']} size={13} className="text-primary" />
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
